import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Button, Stack, Alert, CircularProgress,
  Chip, Divider, LinearProgress, IconButton, Tooltip, Dialog, DialogTitle,
  DialogContent, DialogActions,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  ArrowBack, Refresh, PowerSettingsNew, SimCard as SimIcon,
  SignalCellularAlt, DataUsage, Phone, Info, Sync,
} from '@mui/icons-material';
import { apiService } from '../../services/api';

const SimCardDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [simCard, setSimCard] = useState<any>(null);
  const [simbaseData, setSimbaseData] = useState<any>(null);
  const [usage, setUsage] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmAction, setConfirmAction] = useState<string | null>(null);
  const [history, setHistory] = useState<any>(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  const fetchData = async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.getSimCard(id);
      const data = res?.data || res;
      setSimCard(data.simCard || data);
      setSimbaseData(data.simbaseData || null);
    } catch (err: any) {
      setError(err.message || 'Error cargando SIM');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsage = async () => {
    if (!id) return;
    try {
      const res = await apiService.getSimCardUsage(id);
      setUsage(res?.data || res);
    } catch {}
  };

  useEffect(() => {
    fetchData();
    fetchUsage();
  }, [id]);

  const handleSync = async () => {
    if (!id) return;
    setSyncing(true);
    try {
      await apiService.syncSimCardWithSimbase(id);
      await fetchData();
      await fetchUsage();
    } catch (err: any) {
      setError(err.message || 'Error sincronizando');
    } finally {
      setSyncing(false);
    }
  };

  const handleAction = async (action: string) => {
    if (!id) return;
    setConfirmAction(null);
    setActionLoading(true);
    try {
      if (action === 'activate') {
        await apiService.activateSimCard(id);
      } else if (action === 'deactivate') {
        await apiService.deactivateSimCard(id);
      } else if (action === 'reset') {
        await apiService.resetSimCard(id);
      }
      await fetchData();
    } catch (err: any) {
      setError(err.message || `Error ejecutando ${action}`);
    } finally {
      setActionLoading(false);
    }
  };

  const statusColor = (s: string) => {
    if (s === 'active' || s === 'enabled') return 'success';
    if (s === 'inactive' || s === 'disabled') return 'error';
    if (s === 'suspended') return 'warning';
    return 'default';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!simCard) {
    return (
      <Box sx={{ py: 4 }}>
        <Alert severity="error">SIM Card no encontrada</Alert>
        <Button onClick={() => navigate('/simcards')} sx={{ mt: 2 }}>Volver</Button>
      </Box>
    );
  }

  const sb = simbaseData || {};
  const isActive = simCard.status === 'active' || sb.state === 'enabled';

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <IconButton onClick={() => navigate('/simcards')}>
          <ArrowBack />
        </IconButton>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            <SimIcon sx={{ mr: 1, verticalAlign: 'middle', color: 'primary.main' }} />
            SIM Card
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
            {simCard.iccid}
          </Typography>
        </Box>
        <Chip
          label={isActive ? 'ACTIVA' : simCard.status?.toUpperCase() || 'DESCONOCIDO'}
          color={statusColor(simCard.status)}
          sx={{ fontWeight: 600, fontSize: '0.9rem', px: 1 }}
        />
        <Tooltip title="Sincronizar con Simbase">
          <IconButton onClick={handleSync} disabled={syncing}>
            {syncing ? <CircularProgress size={20} /> : <Sync />}
          </IconButton>
        </Tooltip>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Grid container spacing={3}>
        {/* Info general */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                <Info sx={{ mr: 1, verticalAlign: 'middle' }} />
                Informacion General
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <DetailRow label="ICCID" value={simCard.iccid} mono />
                  <DetailRow label="IMSI" value={simCard.imsi || sb.imsi || '-'} mono />
                  <DetailRow label="MSISDN (Numero)" value={simCard.msisdn || simCard.phoneNumber || sb.msisdn || '-'} />
                  <DetailRow label="Operador" value={simCard.carrier || sb.operator?.name || sb.carrier || '-'} />
                  <DetailRow label="Plan" value={(typeof simCard.plan === 'object' ? simCard.plan?.name : simCard.plan) || sb.plan?.name || sb.plan_id || '-'} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <DetailRow label="Estado Local" value={simCard.status} chip chipColor={statusColor(simCard.status)} />
                  <DetailRow
                    label="Estado Simbase"
                    value={sb.state || sb.status || 'No disponible'}
                    chip
                    chipColor={statusColor(sb.state || sb.status || '')}
                  />
                  <DetailRow label="Simbase ID" value={simCard.simbaseId || sb.id || '-'} mono />
                  <DetailRow label="IP" value={sb.ip || sb.ipAddress || '-'} mono />
                  <DetailRow label="APN" value={sb.apn || '-'} />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Data Usage */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                <DataUsage sx={{ mr: 1, verticalAlign: 'middle' }} />
                Consumo de Datos
              </Typography>
              {(() => {
                const du = simCard.dataUsage?.currentPeriod || usage?.usage || sb.usage || {};
                const usedMB = (du.used || du.dataUsed || 0) / (1024 * 1024);
                const limitMB = (du.limit || du.dataLimit || 0) / (1024 * 1024);
                const pct = limitMB > 0 ? Math.min((usedMB / limitMB) * 100, 100) : 0;

                return (
                  <Box>
                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Usado: {usedMB.toFixed(2)} MB
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {limitMB > 0 ? `Limite: ${limitMB.toFixed(0)} MB` : 'Sin limite'}
                      </Typography>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={pct}
                      sx={{ height: 10, borderRadius: 5, mb: 1 }}
                      color={pct > 90 ? 'error' : pct > 70 ? 'warning' : 'primary'}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {pct > 0 ? `${pct.toFixed(1)}% utilizado` : 'Sin datos de consumo'}
                    </Typography>
                    {du.lastReset && (
                      <Typography variant="caption" color="text.secondary">
                        Ultimo reset: {new Date(du.lastReset).toLocaleDateString('es-DO')}
                      </Typography>
                    )}
                  </Box>
                );
              })()}
            </CardContent>
          </Card>

          {/* Simbase Raw Data */}
          {sb && Object.keys(sb).length > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  <SignalCellularAlt sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Datos de Simbase (en vivo)
                </Typography>
                <Grid container spacing={2}>
                  {Object.entries(sb).map(([key, val]: [string, any]) => {
                    if (typeof val === 'object' && val !== null) return null;
                    return (
                      <Grid size={{ xs: 12, sm: 6, md: 4 }} key={key}>
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                          {key.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ')}
                        </Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {String(val)}
                        </Typography>
                      </Grid>
                    );
                  })}
                </Grid>
                {sb.operator && typeof sb.operator === 'object' && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">Operador</Typography>
                    {Object.entries(sb.operator).map(([k, v]: [string, any]) => (
                      <Typography key={k} variant="body2">
                        <strong>{k}:</strong> {String(v)}
                      </Typography>
                    ))}
                  </Box>
                )}
              </CardContent>
            </Card>
          )}

          {/* History / CDRs / Billing (on-demand) */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  <Phone sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Historial Simbase (30 dias)
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={historyLoading ? <CircularProgress size={16} /> : <Refresh />}
                  onClick={async () => {
                    if (!id) return;
                    setHistoryLoading(true);
                    try {
                      const res = await apiService.getSimCardCdrs(id);
                      setHistory(res?.data || res);
                    } catch {}
                    setHistoryLoading(false);
                  }}
                  disabled={historyLoading}
                >
                  {history ? 'Actualizar' : 'Cargar'}
                </Button>
              </Stack>
              {!history ? (
                <Typography variant="body2" color="text.secondary">
                  Presiona "Cargar" para obtener CDRs y billing de Simbase
                </Typography>
              ) : (
                <Box>
                  {history.errors && history.errors.length > 0 && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      {history.errors.join(' | ')}
                    </Alert>
                  )}
                  {history.billing ? (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>Billing</Typography>
                      {typeof history.billing === 'object' ? (
                        <Grid container spacing={1}>
                          {Object.entries(history.billing).map(([k, v]: [string, any]) => {
                            if (typeof v === 'object' && v !== null) return null;
                            return (
                              <Grid size={{ xs: 6 }} key={k}>
                                <Typography variant="caption" color="text.secondary">{k}</Typography>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                  {v != null ? String(v) : 'No disponible'}
                                </Typography>
                              </Grid>
                            );
                          })}
                        </Grid>
                      ) : (
                        <Typography variant="body2" color="text.secondary">No disponible</Typography>
                      )}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Billing: No disponible</Typography>
                  )}
                  {history.cdrs ? (
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>CDRs (Registros de uso)</Typography>
                      {Array.isArray(history.cdrs) && history.cdrs.length > 0 ? (
                        <Box sx={{ maxHeight: 250, overflowY: 'auto' }}>
                          {history.cdrs.slice(0, 50).map((cdr: any, i: number) => (
                            <Box key={i} sx={{ py: 0.5, borderBottom: '1px solid #eee' }}>
                              <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                {cdr.date || cdr.timestamp || cdr.startTime || '-'} | {cdr.type || cdr.service || '-'} | {cdr.duration || cdr.volume || cdr.bytes || '-'}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">Sin registros en este periodo</Typography>
                      )}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">CDRs: No disponible</Typography>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Sidebar */}
        <Grid size={{ xs: 12, md: 4 }}>
          {/* Actions */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Acciones</Typography>
              <Stack spacing={1.5}>
                {isActive ? (
                  <Button
                    variant="outlined"
                    color="error"
                    fullWidth
                    startIcon={<PowerSettingsNew />}
                    onClick={() => setConfirmAction('deactivate')}
                    disabled={actionLoading}
                  >
                    Desactivar SIM
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    color="success"
                    fullWidth
                    startIcon={<PowerSettingsNew />}
                    onClick={() => setConfirmAction('activate')}
                    disabled={actionLoading}
                  >
                    Activar SIM
                  </Button>
                )}
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<Refresh />}
                  onClick={() => setConfirmAction('reset')}
                  disabled={actionLoading}
                >
                  Reset Conexion
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<Sync />}
                  onClick={handleSync}
                  disabled={syncing}
                >
                  Sincronizar Simbase
                </Button>
              </Stack>
            </CardContent>
          </Card>

          {/* Device linked */}
          {simCard.deviceId && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>Dispositivo Asignado</Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {simCard.deviceId.imei || simCard.deviceId}
                </Typography>
                {simCard.deviceId.name && (
                  <Typography variant="body2">{simCard.deviceId.name}</Typography>
                )}
                {simCard.deviceId._id && (
                  <Button
                    size="small"
                    sx={{ mt: 1 }}
                    onClick={() => navigate(`/devices/${simCard.deviceId._id}`)}
                  >
                    Ver Dispositivo
                  </Button>
                )}
              </CardContent>
            </Card>
          )}

          {/* Client linked */}
          {simCard.clientId && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>Cliente</Typography>
                <Typography variant="body1">{simCard.clientId.name || simCard.clientId}</Typography>
                {simCard.clientId._id && (
                  <Button
                    size="small"
                    sx={{ mt: 1 }}
                    onClick={() => navigate(`/clients/${simCard.clientId._id}`)}
                  >
                    Ver Cliente
                  </Button>
                )}
              </CardContent>
            </Card>
          )}

          {/* Dates */}
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>Fechas</Typography>
              <DetailRow label="Activacion" value={simCard.activationDate ? new Date(simCard.activationDate).toLocaleDateString('es-DO') : '-'} />
              <DetailRow label="Expiracion" value={simCard.expirationDate ? new Date(simCard.expirationDate).toLocaleDateString('es-DO') : '-'} />
              <DetailRow label="Registrada" value={simCard.createdAt ? new Date(simCard.createdAt).toLocaleDateString('es-DO') : '-'} />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Confirm Dialog */}
      <Dialog open={!!confirmAction} onClose={() => setConfirmAction(null)}>
        <DialogTitle>
          {confirmAction === 'activate' ? 'Activar SIM' :
           confirmAction === 'deactivate' ? 'Desactivar SIM' : 'Reset Conexion'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {confirmAction === 'activate' && 'Activar esta SIM en Simbase. El dispositivo comenzara a reportar.'}
            {confirmAction === 'deactivate' && 'Desactivar esta SIM en Simbase. El dispositivo dejara de reportar.'}
            {confirmAction === 'reset' && 'Resetear la conexion de esta SIM. Util para resolver problemas de conectividad.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmAction(null)}>Cancelar</Button>
          <Button
            onClick={() => handleAction(confirmAction!)}
            variant="contained"
            color={confirmAction === 'deactivate' ? 'error' : 'primary'}
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const DetailRow: React.FC<{
  label: string;
  value: string;
  mono?: boolean;
  chip?: boolean;
  chipColor?: any;
}> = ({ label, value, mono, chip, chipColor }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    {chip ? (
      <Box><Chip label={value} size="small" color={chipColor || 'default'} /></Box>
    ) : (
      <Typography variant="body2" sx={mono ? { fontFamily: 'monospace' } : {}}>
        {value}
      </Typography>
    )}
  </Box>
);

export default SimCardDetailPage;
